import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    fetchProjectByIdApi,
    progressProjectApi,
    uploadProjectFileApi,
    fetchProjectFilesApi,
    deleteProjectFileApi,
    downloadProjectFileApi
} from "../../services/api/boq/projectService";
import {
    fetchBoqByProject,
    uploadBoqFileApi,
    fetchBoqFilesApi,
    deleteBoqFileApi,
    viewBoqFileApi,
    downloadBoqHeaderFilesApi
} from "../../services/api/boq/boqService";
import { fetchClientsApi } from "../../services/api/boq/clientService";
import { FaEdit, FaEye } from "react-icons/fa";
import BoqModal from "../../components/boq/BoqModal";
import { toast } from "react-hot-toast";
import { FaUpload, FaDownload, FaTrash } from "react-icons/fa";
import Modal from "react-modal";

export default function ProjectView() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [project, setProject] = useState(null);
    const [boqs, setBoqs] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);

    // BOQ modal state
    const [showBoqModal, setShowBoqModal] = useState(false);
    const [editingBoq, setEditingBoq] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteBoqFileId, setDeleteBoqFileId] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [projectProgress, setProjectProgress] = useState(0);

    // Upload document
    const [files, setFiles] = useState([]);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleteFileId, setDeleteFileId] = useState(null);

    const [showBoqAttachmentModal, setShowBoqAttachmentModal] = useState(false);
    const [selectedBoq, setSelectedBoq] = useState(null);
    const [boqAttachmentFile, setBoqAttachmentFile] = useState(null);
    const [boqAttachments, setBoqAttachments] = useState([]);
    const [boqUploadLoading, setBoqUploadLoading] = useState(false);

    // ---------------- LOAD ALL DATA ----------------
    useEffect(() => {
        loadAll();
        if (id) {
            loadProjectProgress();
            loadFiles();
        }
    }, [id]);

    const loadAll = async () => {
        setLoading(true);
        try {
            const projectRes = await fetchProjectByIdApi(id);
            setProject(projectRes?.data || null);

            const clientsRes = await fetchClientsApi();
            setClients(Array.isArray(clientsRes) ? clientsRes : clientsRes?.data || []);

            await loadBoqs();
        } catch (err) {
            console.error("PROJECT API ERROR:", err);
            toast.error(err.response?.data?.message || "Failed to load projects");
        } finally {
            setLoading(false);
        }
    };

    const loadFiles = async () => {
        try {
            const res = await fetchProjectFilesApi(id);
            setFiles(Array.isArray(res) ? res : res?.data || []);
        } catch (err) {
            console.error(err);
            setFiles([]);
        }
    };

    const handleUploadFile = async (e) => {
        e.preventDefault();

        if (!uploadFile) {
            toast.error("Please select file");
            return;
        }

        const formData = new FormData();
        formData.append("file", uploadFile);

        try {
            setUploadLoading(true);

            await uploadProjectFileApi(id, formData);

            toast.success("File uploaded successfully");

            setUploadFile(null);
            setShowUploadModal(false);

            loadFiles();
        } catch (err) {
            console.error(err);
            toast.error("Upload failed");
        } finally {
            setUploadLoading(false);
        }
    };

    const handleDownloadFile = async (file) => {
        try {
            const response = await downloadProjectFileApi(file.id);

            const blob = response.data;
            console.log("File blob:", blob);
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = file.original_name || "download";

            document.body.appendChild(a);
            a.click();

            a.remove();
            window.URL.revokeObjectURL(url);

        } catch (error) {
            toast.error("Download failed");
        }
    };


    const handleDeleteFile = async () => {
        try {
            await deleteProjectFileApi(deleteFileId);
            toast.success("File deleted");

            setDeleteModalOpen(false);
            setDeleteFileId(null);

            loadFiles();
        } catch (err) {
            toast.error("Delete failed");
        }
    };


    const handleDelete = async () => {
        try {
            await deleteBoqFileApi(deleteBoqFileId);
            toast.success("Boq File Deleted");

            setShowDeleteModal(false);
            setSelectedFile(null);
            setDeleteBoqFileId(null);
            await loadBoqAttachments(selectedFile?.boq_id);
        } catch (err) {
            toast.error("Delete Failed");
        }
    };

    const loadBoqAttachments = async (boqId) => {
        try {
            const res = await fetchBoqFilesApi(boqId);
            setBoqAttachments(res?.data?.boq?.files || []);
        } catch (err) {
            console.error(err);
            setBoqAttachments([]);
        }
    };

    const handleViewFile = async (id) => {
        try {
            const res = await viewBoqFileApi(id);

            const file = new Blob([res.data], {
                type: res.headers["content-type"] || "application/pdf",
            });

            const fileURL = URL.createObjectURL(file);

            window.open(fileURL, "_blank");
        } catch (err) {
            console.error(err);
        }
    };

    const handleDownladFile = async (id) => {
        try {
            const res = await downloadBoqHeaderFilesApi(id);

            const blob = new Blob([res.data], {
                type: res.headers["content-type"],
            });

            const url = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;

            // filename from backend header if available
            const contentDisposition = res.headers["content-disposition"];
            let fileName = "downloaded-file";

            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?(.+)"?/);
                if (match?.[1]) {
                    fileName = match[1];
                }
            }

            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();

            link.remove();
            window.URL.revokeObjectURL(url);

        } catch (err) {
            console.error("Download failed:", err);
        }
    };

    const openBoqAttachmentModal = async (boq) => {
        setSelectedBoq(boq);
        setShowBoqAttachmentModal(true);
        await loadBoqAttachments(boq.id);
    };

    const handleBoqAttachmentUpload = async (e) => {
        e.preventDefault();

        if (!boqAttachmentFile || !selectedBoq) {
            toast.error("Select file");
            return;
        }

        const formData = new FormData();
        formData.append("file", boqAttachmentFile);
        formData.append("boq_id", selectedBoq.id);

        try {
            setBoqUploadLoading(true);

            await uploadBoqFileApi(selectedBoq.id, formData);

            toast.success("Uploaded successfully");

            setBoqAttachmentFile(null);

            loadBoqAttachments(selectedBoq.id);

        } catch (err) {
            toast.error("Upload failed");
        } finally {
            setBoqUploadLoading(false);
        }
    };

    const loadBoqs = async () => {
        try {
            const data = await fetchBoqByProject(id);
            setBoqs(Array.isArray(data) ? data : []);

        } catch (err) {
            console.error(err);
            setBoqs([]);
        }
    };


    const loadProjectProgress = async () => {
        try {
            const data = await progressProjectApi(id);
            setProjectProgress(data.progress_percent ?? 0);
        } catch (err) {
            console.error("Progress fetch failed", err);
            setProjectProgress(0);
        }
    };



    // ---------------- HELPERS ----------------
    const getClientName = (clientId) => {
        const client = clients.find((c) => c.id === clientId);
        return client ? client.client_name || client.name : "—";
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="flex flex-col items-center gap-3">
                    <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-sm text-gray-600">Loading project...</p>
                </div>
            </div>
        );
    }

    if (!project) return <p className="p-1 text-red-600">Project not found</p>;

    return (
        <div className="p-1 bg-gray-100 min-h-screen">
            {/* ================= PROJECT INFO ================= */}
            <div className="bg-white rounded-xl shadow p-3 mb-6">
                <div className="flex justify-between items-center">
                    <h2 className="text-2xl font-bold">{project.project_name}</h2>
                    <button
                        onClick={() => navigate(`/projects/edit/${project.id}`)}
                        className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded flex items-center gap-1"
                    >
                        <FaEdit size={14} /> Edit
                    </button>
                </div>

                <p className="text-sm text-gray-500 mb-4">{project.project_code}</p>

                <div className="grid grid-cols-2 gap-4 text-sm">
                    <p><strong>Client:</strong> {getClientName(project.client_id)}</p>
                    <p><strong>Type:</strong> {project.project_type}</p>
                    <p><strong>Start Date:</strong> {new Date(project.start_date).toLocaleDateString()}</p>
                    <p><strong>End Date:</strong> {new Date(project.end_date).toLocaleDateString()}</p>
                    <p><strong>Value:</strong> ₹{project.project_value}</p>
                    <p><strong>Approved Budget:</strong> ₹{project.approved_budget}</p>
                    <p><strong>Total Amount:</strong> ₹{project.total_amount}</p>
                    <p><strong>Status:</strong> {project.status}</p>
                    <p><strong>Manager:</strong> {project.manager_name || "—"}</p>

                    <div className="bg-white p-4 ">
                        <p className="text-sm text-gray-500">Project Progress</p>
                        <div className="w-full bg-gray-200 h-3 rounded">
                            <div
                                className="bg-green-600 h-3 rounded"
                                style={{ width: `${project.progress_percent}%` }}
                            />
                        </div>
                        <p className="text-sm mt-1">{project.progress_percent}% completed</p>
                    </div>
                </div>

                <div className="mt-4">
                    <strong>Description:</strong>
                    <p className="text-gray-600">{project.description || "No description"}</p>
                </div>
            </div>

            {/* ================= BOQ TABLE ================= */}
            <div className="bg-white rounded-xl shadow p-3">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-semibold">BOQs</h3>

                    <button
                        onClick={() => {
                            setEditingBoq(null);
                            setShowBoqModal(true);
                        }}
                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                        + Create BOQ
                    </button>
                    
                </div>


                {boqs.length === 0 ? (
                    <p className="text-gray-500">No BOQs found for this project.</p>
                ) : (
                    <>
                        {/* ================= MOBILE VIEW ================= */}
                        <div className="space-y-4 md:hidden">
                            {boqs.map((b) => (
                                <div
                                    key={b.id}
                                    className="border rounded-lg p-4 bg-gray-50"
                                >
                                    <h4 className="font-semibold text-lg">{b.boq_name}</h4>

                                    <div className="text-sm text-gray-600 space-y-1 mt-2">
                                        <p><strong>Discipline:</strong> {b.discipline}</p>
                                        <p><strong>Amount:</strong> ₹{b.total_amount ?? 0}</p>
                                        <p><strong>Status:</strong> {b.status}</p>
                                    </div>

                                    <div className="flex gap-2 mt-4">
                                        <button
                                            onClick={() => {
                                                setEditingBoq(b);
                                                setShowBoqModal(true);
                                            }}
                                            className="flex-1 py-2 border rounded flex items-center justify-center gap-1"
                                        >
                                            <FaEdit size={12} /> Edit
                                        </button>

                                        <button
                                            onClick={() => navigate(`/boqs/${b.id}`)}
                                            className="flex-1 py-2 border rounded flex items-center justify-center gap-1"
                                        >
                                            <FaEye size={12} /> View
                                        </button>
                                        <button
                                            onClick={() => openBoqAttachmentModal(b)}
                                            className="px-2 py-1 bg-purple-100 text-purple-700 rounded flex items-center gap-1"
                                        >
                                            <FaUpload size={12} /> Attachment
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* ================= DESKTOP TABLE ================= */}
                        <div className="hidden md:block">
                            <table className="w-full text-sm border p-10">
                                <thead className="bg-gray-100 text-gray-700">
                                    <tr>
                                        <th className="px-4 py-2 border">#</th>
                                        <th className="px-4 py-2 border">BOQ Name</th>
                                        <th className="px-4 py-2 border">Discipline</th>
                                        <th className="px-4 py-2 border">Amount</th>
                                        <th className="px-4 py-2 border">Status</th>
                                        <th className="px-4 py-2 border">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {boqs.map((b, i) => (
                                        <tr key={b.id} className="hover:bg-gray-50 text-center">
                                            <td className="px-4 py-2 border">{i + 1}</td>
                                            <td className="px-4 py-2 border">{b.boq_name}</td>
                                            <td className="px-4 py-2 border">{b.discipline}</td>
                                            <td className="px-4 py-2 border">₹{b.total_amount ?? 0}</td>
                                            <td className="px-4 py-2 border">{b.status}</td>
                                            <td className="px-4 py-2 border flex gap-2 justify-center">
                                                <button
                                                    onClick={() => {
                                                        setEditingBoq(b);
                                                        setShowBoqModal(true);
                                                    }}
                                                    className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded flex items-center gap-1"
                                                >
                                                    <FaEdit size={12} /> Edit
                                                </button>

                                                <button
                                                    onClick={() => navigate(`/boqs/${b.id}`)}
                                                    className="px-2 py-1 bg-blue-100 text-blue-700 rounded flex items-center gap-1"
                                                >
                                                    <FaEye size={12} /> View
                                                </button>
                                                <button
                                                    onClick={() => openBoqAttachmentModal(b)}
                                                    className="px-2 py-1 bg-purple-100 text-purple-700 rounded flex items-center gap-1"
                                                >
                                                    <FaUpload size={12} /> Attachment
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}



            </div>

            {/* ================= DOCUMENTS ================= */}
            <div className="bg-white rounded-xl shadow p-4 mt-6">

                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-semibold">Project Related Documents / Attachments</h3>

                    <button
                        onClick={() => setShowUploadModal(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded flex items-center gap-2"
                    >
                        <FaUpload size={12} /> Upload File
                    </button>
                </div>

                {files.length === 0 ? (
                    <p className="text-gray-500">No files uploaded.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm border">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="px-3 py-2 border">#</th>
                                    <th className="px-3 py-2 border">File Name</th>
                                    <th className="px-3 py-2 border">Uploaded</th>
                                    <th className="px-3 py-2 border">Actions</th>
                                </tr>
                            </thead>

                            <tbody>
                                {files.map((file, i) => (
                                    <tr key={file.id} className="hover:bg-gray-50 text-center">
                                        <td className="px-3 py-2 border">{i + 1}</td>

                                        <td className="px-3 py-2 border">
                                            {file.file_name}
                                        </td>

                                        <td className="px-3 py-2 border">
                                            {new Date(file.created_at).toLocaleDateString()}
                                        </td>

                                        <td className="px-3 py-2 border flex gap-2 justify-center">

                                            <button
                                                onClick={() => handleDownloadFile(file)}
                                                className="px-2 py-1 bg-blue-100 text-blue-700 rounded"
                                            >
                                                <FaDownload size={12} />
                                            </button>

                                            <button
                                                onClick={() => {
                                                    setDeleteFileId(file.id);
                                                    setDeleteModalOpen(true);
                                                }}
                                                className="px-2 py-1 bg-red-100 text-red-700 rounded"
                                            >
                                                <FaTrash size={12} />
                                            </button>

                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <Modal
                isOpen={showBoqAttachmentModal}
                onRequestClose={() => setShowBoqAttachmentModal(false)}
                className="bg-white p-6 rounded-lg max-w-3xl mx-auto mt-20 shadow"
                overlayClassName="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-start"
            >
                <h2 className="text-lg font-semibold mb-4">
                    BOQ Header Attachments
                </h2>

                <div className="mb-4">
                    <select
                        value={selectedBoq?.id || ""}
                        className="border p-2 rounded w-full"
                        disabled
                    >
                        <option>{selectedBoq?.boq_name}</option>
                    </select>
                </div>

                {/* Files Table */}
                <table className="w-full border text-sm mb-4">
                    <thead className="bg-gray-100">
                        <tr>
                            <th className="border px-3 py-2">#</th>
                            <th className="border px-3 py-2">File Name</th>
                            <th className="border px-3 py-2">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {boqAttachments.map((file, i) => (
                            <tr key={file.id}>
                                <td className="border px-3 py-2">{i + 1}</td>
                                <td className="border px-3 py-2">{file.file_name}</td>
                                <td className="border px-3 py-2 flex gap-2 justify-center">

                                    <button
                                        onClick={() => handleDownladFile(file.id)}
                                        className="px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
                                        title="Download File"
                                    >
                                        <FaDownload size={12} />
                                    </button>
                                    <button
                                        onClick={() => handleViewFile(file.id)}
                                        className="px-2 py-1 bg-blue-100 text-blue-700 rounded"
                                    >
                                        <FaEye size={12} />
                                    </button>
                                    <button
                                        onClick={() => {
                                            setSelectedFile(file);
                                            setDeleteBoqFileId(file.id);
                                            setShowDeleteModal(true);
                                        }}
                                        className="px-2 py-1 bg-red-100 text-red-700 rounded"
                                    >
                                        <FaTrash size={12} />
                                    </button>

                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* Upload */}
                <form onSubmit={handleBoqAttachmentUpload}>
                    <input
                        type="file"
                        onChange={(e) => setBoqAttachmentFile(e.target.files[0])}
                        className="border p-2 rounded w-full mb-4"
                    />

                    <div className="flex justify-end gap-2">
                        <button
                            type="button"
                            onClick={() => setShowBoqAttachmentModal(false)}
                            className="px-4 py-2 border rounded"
                        >
                            Close
                        </button>

                        <button
                            type="submit"
                            disabled={boqUploadLoading}
                            className="px-4 py-2 bg-blue-600 text-white rounded"
                        >
                            {boqUploadLoading ? "Uploading..." : "Upload"}
                        </button>
                    </div>
                </form>
            </Modal>

            <Modal
                isOpen={showUploadModal}
                onRequestClose={() => setShowUploadModal(false)}
                className="bg-white p-6 rounded-lg max-w-md mx-auto mt-40 shadow"
                overlayClassName="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-start"
            >
                <h2 className="text-lg font-semibold mb-4">Upload Document</h2>

                <form onSubmit={handleUploadFile} className="space-y-4">

                    <input
                        type="file"
                        onChange={(e) => setUploadFile(e.target.files[0])}
                        className="border p-2 w-full rounded"
                    />

                    <div className="flex justify-end gap-2">

                        <button
                            type="button"
                            onClick={() => setShowUploadModal(false)}
                            className="px-4 py-2 border rounded"
                        >
                            Cancel
                        </button>

                        <button
                            type="submit"
                            disabled={uploadLoading}
                            className="px-4 py-2 bg-blue-600 text-white rounded"
                        >
                            {uploadLoading ? "Uploading..." : "Upload"}
                        </button>

                    </div>
                </form>
            </Modal>

            <Modal
                isOpen={deleteModalOpen}
                onRequestClose={() => setDeleteModalOpen(false)}
                className="bg-white p-6 rounded-lg max-w-sm mx-auto mt-40 shadow"
                overlayClassName="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-start"
            >
                <h2 className="text-lg font-semibold mb-3 text-red-600">
                    Delete File
                </h2>

                <p className="text-gray-600 mb-5">
                    Are you sure you want to delete this file?
                </p>

                <div className="flex justify-end gap-2">

                    <button
                        onClick={() => setDeleteModalOpen(false)}
                        className="px-4 py-2 border rounded"
                    >
                        Cancel
                    </button>

                    <button
                        onClick={handleDeleteFile}
                        className="px-4 py-2 bg-red-600 text-white rounded"
                    >
                        Delete
                    </button>

                </div>
            </Modal>

            {/* delete the boq header files */}
            {showDeleteModal && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 w-80">
                        <h2 className="text-lg font-semibold mb-4">Confirm Delete</h2>
                        <p className="text-sm text-gray-600 mb-4">
                            Are you sure you want to delete
                        </p>

                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                className="px-4 py-2 bg-gray-200 rounded"
                            >
                                Cancel
                            </button>

                            <button
                                onClick={handleDelete}
                                className="px-4 py-2 bg-red-600 text-white rounded"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ================= BOQ MODAL ================= */}
            <BoqModal
                open={showBoqModal}
                onClose={() => setShowBoqModal(false)}
                projectId={project.id}
                boq={editingBoq}
                onSaved={loadBoqs}
            />
        </div>


    );
}
